import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import {
  ExtractionInProgressError,
  getFileAnalyseResultApi,
  getFilePreviewApi,
  startFileAnalyseApi,
  type FilePreviewResponse,
  type StartFileAnalyseRequest
} from '@/api/endpoints/filePreview';
import { asContainerId } from '@/types/brandedIds';
import { addImportDefinitionColumns } from '@/api/endpoints/importDefinitionConfig';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useUploadStore } from '@/store/storeUpload';
import { useGlobalStore } from '@/store/storeGlobal';
import { extractErrorMessage } from '@/helpers/errorHandlingHelpers';

interface FileConfig {
  previewLines: number;
  analysisLines: number;
  searchQuery: string | null;
  separator: string;
  escape: string;
  quote: string;
  encoding: string;
  headerRow: number;
  firstDataRow: number;
}

interface PreviewFetchResult {
  success: boolean;
  resolvedUploadId?: string;
  awaitingSelection?: boolean;
  awaitingExtraction?: boolean;
  error?: string;
}

interface AuthoritativeColumnDefinition {
  sourceColumnName?: string;
  columnName: string;
  columnLabel: string;
  primaryFieldType?: string;
}

type AnalyseProgressUpdate = {
  jobId?: string;
  status?: string;
  hasError?: boolean;
  errorMessage?: string;
  message?: string;
  progressPercent?: number;
};

type AnalyseProgressEvent = {
  detail?: AnalyseProgressUpdate;
};

type AnalysePromiseResolve = ((value: boolean) => void) | null;
type AnalysePromiseReject = ((reason?: unknown) => void) | null;

const ZIP_CONTENT_TYPES = ['application/zip', 'application/x-zip-compressed'];

export const useFilePreviewStore = defineStore('filePreviewStore', () => {
  const globalStore = useGlobalStore();
  const notificationsStore = useNotificationsStore();
  const uploadStore = useUploadStore();
  const { files } = storeToRefs(uploadStore);

  const debugPrefix = '[FilePreview]';

  const preview = ref<FilePreviewResponse | null>(null);
  const analyse = ref<any>(null);
  // WI 1832: BE-analysen er sannheten for kolonne-avstemmingen. Når header-/datarad-
  // konfigurasjonen endres må vi kjøre en FERSK analyse og vise «analyserer …» imens —
  // aldri en FE-fallback. `isAnalysing` styrer den reaktive «analyserer …»-tilstanden,
  // og `lastAnalyseRequest` lar oss spille av samme analyse-parametre etter en
  // preview-refresh (header/datarad-endring) uten watch().
  // WI 2317: `analyse` ER ferskhets-sannheten — `getFilePreview` nuller den på
  // ENHVER fetch. Er den ikke-null, gjelder den arket som nettopp ble previewet.
  // Den gamle `analysedUploadId`-ferskhetsgaten (sammenlignet mot en FE-holdt
  // upload-ref) er fjernet sammen med de pairwise-synkede ark-referansene.
  const isAnalysing = ref(false);
  const lastAnalyseRequest = ref<{ tableKey: string | null; importDefinitionKey: string | null } | null>(null);
  const fileConfig = ref<FileConfig>({
    previewLines: 50,
    analysisLines: 10000,
    searchQuery: null,
    separator: '|',
    escape: '\\',
    quote: '"',
    encoding: 'utf-8',
    headerRow: 1,
    firstDataRow: 2
  });
  const selectedFileName = ref('');
  const isSelectedFileTextFile = ref(false);
  const errorReadingFile = ref(false);
  const errorMessage = ref('');
  const isFetchingPreview = ref(false);
  const awaitingExtraction = ref(false);
  const awaitingZipSelection = ref(false);
  const zipFileOptions = ref<Array<{ fileName: string; uploadId: string }>>([]);
  const lastFetchedPreviewForUploadId = ref<string | null>(null);
  const authoritativeColumnDefinitions = ref<AuthoritativeColumnDefinition[]>([]);

  // #1820: overskrifts-/datarad er bruker-styrt visnings-state over RÅ preview-
  // rader — ikke en BE-detektert filegenskap. Når brukeren eksplisitt har satt
  // dem, må en påfølgende preview-fetch som bærer GAMMEL (stale) metadata IKKE
  // klobbe valget (symptomet «spretter tilbake til 1/2»). Vi sporer hvilken
  // upload bruker-valget gjelder, så setValues seeder header/datarad fra metadata
  // KUN ved første preview for en upload (draft-restore / fil-last), og bevarer
  // bruker-overstyringen ellers. Bytter uploaden, nullstilles markøren (ny fil
  // seeder fra sin egen metadata).
  const userRowConfigUploadId = ref<string | null>(null);

  // WI 1832: monotont token slik at en avbrutt/eldre analyse ikke nullstiller
  // «analyserer …»-flagget som en nyere analyse nettopp har satt.
  let analyseToken = 0;
  let activeAnalyseListener: ((event: Event) => void) | null = null;
  let activeAnalyseTimeout: ReturnType<typeof setTimeout> | null = null;
  let activeAnalyseResolve: AnalysePromiseResolve = null;
  let activeAnalyseReject: AnalysePromiseReject = null;
  let activePreviewAbortController: AbortController | null = null;

  function clearActiveAnalyseHandlers (): void {
    if (activeAnalyseListener) {
      globalThis.removeEventListener('analyseProgress', activeAnalyseListener);
      activeAnalyseListener = null;
    }
    if (activeAnalyseTimeout) {
      clearTimeout(activeAnalyseTimeout);
      activeAnalyseTimeout = null;
    }
    activeAnalyseResolve = null;
    activeAnalyseReject = null;
  }

  function resetAnalyseState (): void {
    analyse.value = null;
    authoritativeColumnDefinitions.value = [];
  }

  // WI 1832: spilles av etter en preview-refresh på SAMME fil (typisk header-/datarad-
  // endring) for å hente en fersk BE-analyse mot den nye konfigurasjonen. Den forrige
  // analysen er allerede invalidert (resetAnalyseState) av getFilePreview, så lenge en
  // re-analyse pågår viser komponenten «analyserer …» i stedet for noe stale/fallback.
  async function reanalyseAfterPreviewRefresh (): Promise<void> {
    const request = lastAnalyseRequest.value;
    if (!request || !preview.value?.uploadId) {
      return;
    }
    await analyseFile(request.tableKey, request.importDefinitionKey);
  }

  function cancelAnalyse (): void {
    if (activeAnalyseReject) {
      activeAnalyseReject(new Error('Analyse avbrutt'));
    }
    clearActiveAnalyseHandlers();
  }

  function normalizeSeparatorForUi (separator?: string | null): string {
    if (separator == null || separator === '') {
      return '|';
    }
    if (separator === '<TAB>' || separator === '\t') {
      return '\t';
    }
    return separator;
  }

  function setDefault (): void {
    fileConfig.value = {
      previewLines: 50,
      analysisLines: 10000,
      searchQuery: null,
      separator: '|',
      escape: '\\',
      quote: '"',
      encoding: 'utf-8',
      headerRow: 1,
      firstDataRow: 2
    };
  }

  function setValues (metadata: FilePreviewResponse['metadata'], previewUploadId: string | null): void {
    // #1820: bevar bruker-satt overskrifts-/datarad for DENNE uploaden. Seeding
    // fra BE-metadata skjer kun når brukeren ikke har overstyrt rader for den
    // aktuelle uploaden — da er metadata (eller defaults) riktig kilde.
    const preserveUserRows =
      !!previewUploadId && userRowConfigUploadId.value === previewUploadId;
    const keptHeaderRow = fileConfig.value.headerRow;
    const keptFirstDataRow = fileConfig.value.firstDataRow;

    if (!metadata) {
      setDefault();
      if (preserveUserRows) {
        fileConfig.value.headerRow = keptHeaderRow;
        fileConfig.value.firstDataRow = keptFirstDataRow;
      }
      return;
    }

    fileConfig.value = {
      ...fileConfig.value,
      separator: normalizeSeparatorForUi(metadata.separator),
      escape: metadata.escape ?? '\\',
      quote: metadata.quote ?? '"',
      encoding: metadata.encoding ?? 'utf-8',
      headerRow: preserveUserRows ? keptHeaderRow : (metadata.headerRow ?? 1),
      firstDataRow: preserveUserRows ? keptFirstDataRow : (metadata.firstDataRow ?? 2)
    };
  }

  // #1820: komponenten kaller denne når brukeren eksplisitt endrer overskrifts-/
  // datarad, slik at en senere preview-fetch ikke klobber valget. Markøren bindes
  // til den aktuelle uploaden (det previewede arket).
  function markUserRowConfig (headerRow: number, firstDataRow: number): void {
    const uploadId = preview.value?.uploadId ?? null;
    userRowConfigUploadId.value = uploadId;
    fileConfig.value.headerRow = headerRow;
    fileConfig.value.firstDataRow = firstDataRow;
  }

  function getMetadata (): Record<string, string | number> {
    return {
      separator: fileConfig.value.separator,
      escape: fileConfig.value.escape,
      quote: fileConfig.value.quote,
      encoding: fileConfig.value.encoding,
      headerRow: fileConfig.value.headerRow,
      firstDataRow: fileConfig.value.firstDataRow
    };
  }

  function hasFreshPreviewForCurrentSelection (): boolean {
    const previewUploadId = preview.value?.uploadId;
    return !!lastFetchedPreviewForUploadId.value &&
      !!previewUploadId &&
      lastFetchedPreviewForUploadId.value === previewUploadId;
  }

  function isZipFile (uploadId: string): boolean {
    const file = uploadStore.getFileByUploadId(uploadId);
    return !!file?.contentType && ZIP_CONTENT_TYPES.includes(file.contentType);
  }

  // Bug 3374: previewLinesOverride lar en kaller be om et annet rad-antall enn det
  // `fileConfig.previewLines` staar paa, UTEN aa skrive til fileConfig. Arkiv-visningen
  // er en utforskning; veiviserens felt er en innstilling paa en importmal. De to skal
  // ikke dele verdi (eier-beslutning 2026-08-16). Utelates argumentet er adferden uendret.
  async function getFilePreview (uploadId: string, previewLinesOverride?: number): Promise<boolean> {
    if (!uploadId) {
      return false;
    }

    // Diagnostisk: BE forventer Guid for uploadId. Hvis caller passerer noe
    // annet (typisk filnavn fra stale state eller buggy emit-handler), throw
    // med stack-trace så vi får isolert kilden — IKKE silent drop (per
    // [memory feedback_never_swallow_be_errors] + [memory feedback_no_assumptions_allowed]).
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uploadId)) {
      console.error(
        `[filePreviewStore.getFilePreview] caller sent non-Guid uploadId: ${JSON.stringify(uploadId)}. Stack-trace:`,
        new Error('non-Guid uploadId').stack,
      );
      throw new Error(`filePreviewStore.getFilePreview: uploadId må være Guid, fikk "${uploadId}"`);
    }

    if (activePreviewAbortController) {
      activePreviewAbortController.abort();
    }
    const abortController = new AbortController();
    activePreviewAbortController = abortController;

    try {
      // WI 2318: preview-grensen tar ContainerId — den opplastede arbeidsboka.
      const requestedPreviewLines = previewLinesOverride ?? fileConfig.value.previewLines;
      const data = await getFilePreviewApi(asContainerId(uploadId), requestedPreviewLines, abortController.signal);
      if (abortController.signal.aborted) {
        return false;
      }
      awaitingExtraction.value = false;
      const hadPriorAnalysis = !!analyse.value;
      const previousPreviewUploadId = preview.value?.uploadId ?? null;
      preview.value = data;
      const resolvedUploadId = data.uploadId ?? null;
      // #1820: bytter den faktiske uploaden (ny fil / annet ark), nullstilles
      // bruker-rad-markøren så den nye filas metadata seeder header/datarad.
      if (userRowConfigUploadId.value && userRowConfigUploadId.value !== resolvedUploadId) {
        userRowConfigUploadId.value = null;
      }
      // WI 2317: en preview-refresh betyr at header-/datarad-konfigurasjonen (eller
      // selve arket) kan ha endret seg → den forrige BE-analysen er ikke lenger gyldig
      // for det vi nå viser. `analyse` ER ferskhets-sannheten: nuller den på ENHVER
      // fetch (ingen `analysedUploadId`-gate lenger). Var det en analyse for SAMME ark
      // (typisk en header-/datarad-endring som re-fetchet samme upload), spiller vi av
      // samme analyse-parametre så komponenten får ferske BE-tall — aldri fallback.
      // Et bytte til et ANNET ark dropper analysen uten replay (wizard-flyten driver
      // sin egen analyseFile for det nye arket).
      const switchedToDifferentUpload =
        !!previousPreviewUploadId &&
        previousPreviewUploadId !== resolvedUploadId;
      const hadPriorAnalysisForThisUpload = hadPriorAnalysis && !switchedToDifferentUpload;
      resetAnalyseState();
      selectedFileName.value = data.fileName ?? '';
      isSelectedFileTextFile.value = !!data.isTextFile;

      setValues(data.metadata, resolvedUploadId);
      errorReadingFile.value = !!data.hasError;
      errorMessage.value = data.errorMessage ?? '';
      globalStore.debug(`${debugPrefix} preview`, preview.value);

      // WI 1832: hadde vi en analyse for nettopp denne filen (typisk en header-/datarad-
      // endring som re-fetchet samme upload), kjør en fersk BE-analyse mot den nye
      // konfigurasjonen. `isAnalysing` (satt i analyseFile) gir komponenten en
      // «analyserer …»-tilstand imens, så den aldri viser stale tall eller en FE-fallback.
      // Bytte til en annen fil drives av wizard-flyten selv (egen analyseFile-kall).
      if (hadPriorAnalysisForThisUpload) {
        void reanalyseAfterPreviewRefresh();
      }
      return true;
    } catch (error: any) {
      if (abortController.signal.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return false;
      }
      if (error instanceof ExtractionInProgressError) {
        awaitingExtraction.value = true;
        errorReadingFile.value = false;
        errorMessage.value = '';
        return false;
      }
      awaitingExtraction.value = false;
      errorReadingFile.value = true;
      errorMessage.value = error?.response?.data?.detail ?? error?.message ?? 'En feil oppstod ved lasting av filen';
      return false;
    } finally {
      if (activePreviewAbortController === abortController) {
        activePreviewAbortController = null;
      }
    }
  }

  // US 1848 task 2058: persistering av fil-metadata + påfølgende preview-refresh er én
  // logisk operasjon som de tre fil-/preview-komponentene (ImportFilePreview,
  // ImportFileMetadata, Step3FileSelection) delte ordrett — inkludert try/catch +
  // addError. Eierskapet flyttet hit (storen) så komponentene blir rene dispatch-lag:
  // de bygger metadata og kaller denne, og kjører kun sin egen suksess-orkestrering
  // (f.eks. draft-sync) når resultatet er { ok: true }. Feil-toasten vises av storen.
  async function updateFileMetadataAndRefreshPreview (
    uploadId: string,
    metadata: unknown
  ): Promise<{ ok: boolean }> {
    try {
      // Oppdater kun fil-metadata på filen
      await uploadStore.updateMetadata(uploadId, metadata);
      // Oppdater forhåndsvisning
      await getFilePreview(uploadId);
      return { ok: true };
    } catch (error: any) {
      notificationsStore.addError('Feil ved oppdatering av filmetadata', extractErrorMessage(error, ''));
      return { ok: false };
    }
  }

  async function fetchPreviewForContainer (
    targetUploadId: string,
    selectedUploadId?: string,
    previewLinesOverride?: number
  ): Promise<PreviewFetchResult> {
    if (!targetUploadId) {
      return { success: false, error: 'Mangler uploadId for forhåndsvisning.' };
    }
    if (isFetchingPreview.value) {
      return { success: false, error: 'Forhåndsvisning pågår allerede.' };
    }

    isFetchingPreview.value = true;
    awaitingZipSelection.value = false;
    awaitingExtraction.value = false;
    zipFileOptions.value = [];
    errorReadingFile.value = false;
    errorMessage.value = '';

    try {
      let requestUploadId = targetUploadId;
      if (isZipFile(targetUploadId)) {
        await uploadStore.getFiles();
        const extractedFiles = (uploadStore.getFilesByExtractedFromUploadId(targetUploadId) ?? []) as Array<{ uploadId: string; fileName: string }>;
        const selectedIsChild = !!selectedUploadId && extractedFiles.some((f: { uploadId: string }) => f.uploadId === selectedUploadId);

        if (selectedIsChild && selectedUploadId) {
          requestUploadId = selectedUploadId;
        } else if (extractedFiles.length === 1) {
          requestUploadId = extractedFiles[0].uploadId;
        } else if (extractedFiles.length > 1) {
          zipFileOptions.value = extractedFiles.map((f: { fileName: string; uploadId: string }) => ({ fileName: f.fileName, uploadId: f.uploadId }));
          awaitingZipSelection.value = true;
          return { success: false, awaitingSelection: true };
        } else {
          return { success: false, error: 'Ingen filer funnet i zip-arkivet.' };
        }
      }

      const ok = await getFilePreview(requestUploadId, previewLinesOverride);
      if (!ok) {
        if (awaitingExtraction.value) {
          return { success: false, awaitingExtraction: true };
        }
        return { success: false, error: errorMessage.value || 'Klarte ikke å hente forhåndsvisning.' };
      }

      const resolvedUploadId = preview.value?.uploadId ?? requestUploadId;
      lastFetchedPreviewForUploadId.value = resolvedUploadId;
      return { success: true, resolvedUploadId };
    } finally {
      isFetchingPreview.value = false;
    }
  }

  async function analyseFile (tableKey?: string | null, importDefinitionKey?: string | null): Promise<boolean> {
    // WI 2317: arket som analyseres er det BE-resolverte arket vi nettopp har
    // previewet — `preview.value.uploadId`. Ingen FE-holdt parallell ark-ref
    // (`selectedFileUploadId` er fjernet). BE krever fortsatt uploadId på wire-en
    // (analyse-endepunktet resolver ikke arket fra draften), men kilden er nå den
    // ENE referansen (det previewede arket), ikke en synket FE-kopi.
    // Bug 3371: no preview has resolved yet, so there is nothing to analyse. Return false and stay
    // silent - announcing this belongs to the caller, not to the store.
    //
    // analyseFile has four callers. Three are user actions and each already turns false into its own
    // message: autoMatch says "Analyse av filen feilet", analyzeCurrentDataset guards up front with
    // "Ingen datasett valgt", and analyseFileForSuggestions throws. The fourth fires automatically
    // from ImportWizardShellView when the file config updates, which is not a user action at all.
    // A toast here therefore told the user no file was selected at the exact moment they had just
    // selected one - the preview simply had not resolved yet. It described neither the real
    // condition nor anything the user could act on.
    const analyseUploadId = preview.value?.uploadId ?? null;
    if (!analyseUploadId) {
      return false;
    }

    const normalizedTableKey = tableKey != null ? String(tableKey).trim() : '';
    const normalizedImportDefinitionKey = importDefinitionKey != null ? String(importDefinitionKey).trim() : '';

    // WI 1832: husk parametrene så en preview-refresh (header-/datarad-endring) kan
    // spille av samme analyse uten at komponenten må kjenne table-/mal-nøkkelen.
    lastAnalyseRequest.value = {
      tableKey: normalizedTableKey || null,
      importDefinitionKey: normalizedImportDefinitionKey || null
    };

    const analysisRequest: StartFileAnalyseRequest = {
      uploadId: analyseUploadId,
      analysisLines: fileConfig.value.analysisLines,
      importDefinitionKey: normalizedImportDefinitionKey || null
    };
    if (normalizedTableKey) {
      analysisRequest.tableKey = normalizedTableKey;
    }
    globalStore.debug(`${debugPrefix} analyseFile`, analysisRequest);

    // WI 1832: reaktiv «analyserer …»-tilstand mens BE jobber. Komponenten bruker
    // dette til å vise et venter-panel i stedet for stale tall / FE-fallback.
    const myToken = ++analyseToken;
    isAnalysing.value = true;
    try {
      cancelAnalyse();

      // Bug 3362: the listener is mounted BEFORE the job is started, and updates that arrive
      // before the jobId is known are buffered and replayed.
      //
      // Completion arrives as a SignalR push relayed to the 'analyseProgress' event. Starting the
      // job first left a window between the server accepting it and this listener existing, and a
      // push landing in that window was lost outright - the caller then waited out the ten minute
      // timeout for a job that had already finished. The window is not theoretical: the push and
      // the start response are both network, so their order is not guaranteed, and a small job can
      // complete before the response reaches the browser. That is why the four column file hung
      // while the fifteen column one merely took long.
      return await new Promise<boolean>((resolve, reject) => {
        let isResolved = false;
        let jobId: string | null = null;
        const bufferedUpdates: AnalyseProgressUpdate[] = [];
        activeAnalyseResolve = resolve;
        activeAnalyseReject = reject;

        const handleProgress = (event: Event): void => {
          if (isResolved) {
            return;
          }
          const progressUpdate = (event as unknown as AnalyseProgressEvent).detail;
          if (!progressUpdate) {
            return;
          }
          if (jobId === null) {
            // The job id is not known yet, so this update cannot be matched or dismissed. Keep it.
            bufferedUpdates.push(progressUpdate);
            return;
          }
          if (progressUpdate.jobId !== jobId) {
            return;
          }
          applyProgress(progressUpdate, jobId);
        };

        const applyProgress = (progressUpdate: AnalyseProgressUpdate, resolvedJobId: string): void => {
          if (isResolved) {
            return;
          }

          globalStore.debug(`${debugPrefix} analyse progress`, progressUpdate);
          if (progressUpdate.status === 'completed') {
            isResolved = true;
            clearActiveAnalyseHandlers();
            getFileAnalyseResultApi(resolvedJobId)
              .then((result) => {
                analyse.value = result;
                globalStore.debug(`${debugPrefix} analyse`, analyse.value);
                notificationsStore.addSuccess('Filen er analysert!');
                resolve(true);
              })
              .catch((err: any) => {
                const detail = err?.response?.data?.detail;
                const message = err?.message;
                const errorMsg = detail || message || 'Kunne ikke hente analyse-resultat';
                notificationsStore.addError('Feil ved henting av analyse', errorMsg);
                reject(new Error(errorMsg));
              });
            return;
          }

          if (progressUpdate.status === 'failed' || progressUpdate.hasError) {
            isResolved = true;
            clearActiveAnalyseHandlers();
            const errorMsg = progressUpdate.errorMessage || progressUpdate.message || 'Ukjent feil ved analyse';
            notificationsStore.addError('Feil ved analyse', errorMsg);
            reject(new Error(errorMsg));
          }
        };

        activeAnalyseListener = handleProgress;
        globalThis.addEventListener('analyseProgress', activeAnalyseListener);
        activeAnalyseTimeout = setTimeout(() => {
          if (isResolved) {
            return;
          }
          isResolved = true;
          clearActiveAnalyseHandlers();
          notificationsStore.addError('Timeout', 'Fil-analyse tok for lang tid');
          reject(new Error('Timeout'));
        }, 600000);

        // Only now is it safe to start the job: any push it triggers has somewhere to land.
        void startFileAnalyseApi(analysisRequest)
          .then((response) => {
            if (isResolved) {
              return;
            }
            if (!response?.jobId) {
              isResolved = true;
              clearActiveAnalyseHandlers();
              resolve(false);
              return;
            }
            jobId = response.jobId;
            globalStore.debug(`${debugPrefix} analyse job started`, { jobId });
            for (const update of bufferedUpdates) {
              if (isResolved) {
                break;
              }
              if (update?.jobId === jobId) {
                applyProgress(update, jobId);
              }
            }
          })
          .catch((err: unknown) => {
            if (isResolved) {
              return;
            }
            isResolved = true;
            clearActiveAnalyseHandlers();
            reject(err);
          });
      });
    } catch (error: any) {
      if (error instanceof Error && error.message === 'Analyse avbrutt') {
        return false;
      }
      const detail = error?.response?.data?.detail;
      const message = error?.message;
      const errorMsg = detail || message || 'Kunne ikke starte fil-analyse';
      notificationsStore.addError('Feil ved analyse', errorMsg);
      return false;
    } finally {
      // Bare den nyeste analysen rydder flagget; en eldre/avbrutt skal ikke
      // slå av «analyserer …» for den som overtok.
      if (myToken === analyseToken) {
        isAnalysing.value = false;
      }
    }
  }

  function clearPreview (): void {
    preview.value = null;
    selectedFileName.value = '';
    isSelectedFileTextFile.value = false;
    errorReadingFile.value = false;
    errorMessage.value = '';
    awaitingZipSelection.value = false;
    awaitingExtraction.value = false;
    zipFileOptions.value = [];
    lastFetchedPreviewForUploadId.value = null;
    authoritativeColumnDefinitions.value = [];
    userRowConfigUploadId.value = null;
  }

  function clearAnalyse (): void {
    cancelAnalyse();
    resetAnalyseState();
    isAnalysing.value = false;
    lastAnalyseRequest.value = null;
  }

  // Bug 2833: komplett Nullstill — filsteg-konfigurasjonen (rad-oppsett/separator/encoding)
  // tilbake til standard + bruker-rad-markøren bort. Egen action (ikke i clearPreview):
  // fil-/arkbytte i en PÅGÅENDE runde skal fortsatt bevare bruker-satt rad-oppsett per
  // opplasting (#1820); kun wizard-reset skal nulle alt.
  function resetFileConfig (): void {
    setDefault();
    userRowConfigUploadId.value = null;
  }

  function setAuthoritativeColumnDefinitions (definitions: AuthoritativeColumnDefinition[]): void {
    authoritativeColumnDefinitions.value = Array.isArray(definitions) ? [...definitions] : [];
  }

  const filteredFiles = computed(() => {
    if (!fileConfig.value.searchQuery) {
      return files.value;
    }
    const search = fileConfig.value.searchQuery.toLowerCase();
    return files.value?.filter((file: { fileName?: string }) => file.fileName?.toLowerCase().includes(search));
  });

  const isFirstDataRowValid = computed(() => {
    return fileConfig.value.firstDataRow > fileConfig.value.headerRow;
  });

  async function addUnlinkedColumns (importDefinitionKey: string): Promise<void> {
    const columnsComparison = analyse.value?.columnsComparison;
    if (!columnsComparison) {
      return;
    }

    await addImportDefinitionColumns(importDefinitionKey, {
      columns: Object.values(columnsComparison)
        .filter((column: any) => column.mappingStatus === 'unable to map')
        .map((column: any) => ({
          sourceColumnName: column.sourceColumnName,
          targetColumnKey: '',
          ordinal: column.fileColumn?.ordinal
        }))
    });
  }

  async function addSuggestedColumns (importDefinitionKey: string): Promise<void> {
    const columnsComparison = analyse.value?.columnsComparison;
    if (!columnsComparison) {
      return;
    }

    await addImportDefinitionColumns(importDefinitionKey, {
      columns: Object.values(columnsComparison)
        .filter((column: any) => column.mappingStatus === 'suggestion')
        .map((column: any) => ({
          sourceColumnName: column.sourceColumnName,
          targetColumnKey: column.tableColumnName,
          ordinal: column.fileColumn?.ordinal
        }))
    });
  }

  return {
    preview,
    analyse,
    isAnalysing,
    reanalyseAfterPreviewRefresh,
    fileConfig,
    selectedFileName,
    isSelectedFileTextFile,
    errorReadingFile,
    errorMessage,
    isFetchingPreview,
    awaitingZipSelection,
    awaitingExtraction,
    zipFileOptions,
    lastFetchedPreviewForUploadId,
    authoritativeColumnDefinitions,
    getFilePreview,
    updateFileMetadataAndRefreshPreview,
    fetchPreviewForContainer,
    hasFreshPreviewForCurrentSelection,
    getMetadata,
    markUserRowConfig,
    analyseFile,
    cancelAnalyse,
    clearPreview,
    clearAnalyse,
    resetFileConfig,
    // Bug 2833: eksponert for reset-verifikasjon (markøren for bruker-satt rad-oppsett).
    userRowConfigUploadId,
    setAuthoritativeColumnDefinitions,
    filteredFiles,
    isFirstDataRowValid,
    addUnlinkedColumns,
    addSuggestedColumns
  };
});
